/**
 * Extra Work Form — Digi Sahyadri WRMS
 * Only available on weekends and public holidays.
 * Includes file upload and benefit configuration.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPaperPlane, FaLock, FaClock, FaUpload, FaInfoCircle } from 'react-icons/fa';
import WRMSFormHeader from '../shared/WRMSFormHeader';
import SignatureFooter from '../shared/SignatureFooter';
import { submitRequest } from '../../services/wrmsService';
import { playNotificationSound } from '../../../utils/soundService';
import { isNonWorkingDay } from '../../services/calendarService';
import { getApprovalChain, getDeptGroup, getStageLabelForRole } from '../../utils/ApprovalRouter';

const WORK_TYPES = [
  { value: 'weekend_work', label: 'Weekend Work' },
  { value: 'public_holiday_work', label: 'Public Holiday Work' },
  { value: 'emergency_work', label: 'Emergency Work' },
  { value: 'extra_shift', label: 'Extra Shift' },
];

const today = () => new Date().toISOString().split('T')[0];

const calcHours = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) return 0;
  return (mins / 60).toFixed(1);
};

const getDayInfo = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const day = d.getDay();
  if (day === 0) return 'Sunday';
  if (day === 6) return 'Saturday';
  return null;
};

const ExtraWorkForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [dayCheck, setDayCheck] = useState({ loading: true, isNonWorking: false, type: '', description: '' });


  const [form, setForm] = useState({
    workDate: today(),
    workType: '',
    startTime: '',
    endTime: '',
    totalHours: 0,
    description: '',
    projectClient: '',
    outputAchieved: '',
  });

  // Check if today/selected date is non-working
  useEffect(() => {
    setDayCheck({ loading: true, isNonWorking: false, type: '', description: '' });
    isNonWorkingDay(form.workDate)
      .then(result => setDayCheck({ loading: false, ...result }))
      .catch(() => {
        // Fallback: just check weekend
        const weekend = getDayInfo(form.workDate);
        setDayCheck({
          loading: false,
          isNonWorking: !!weekend,
          type: weekend ? 'weekend' : 'working_day',
          description: weekend || 'Working Day',
        });
      });
  }, [form.workDate]);

  // Auto-calc hours
  useEffect(() => {
    setForm(prev => ({ ...prev, totalHours: calcHours(prev.startTime, prev.endTime) }));
  }, [form.startTime, form.endTime]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.workType || !form.startTime || !form.endTime || !form.description) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      const result = await submitRequest('extra_work', payload, user);
      playNotificationSound();
      toast.success(`Extra work submitted! ID: ${result.requestId}`);
      navigate('/wrms');
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const deptGroup = getDeptGroup(user?.department, user?.role);
  const chain = getApprovalChain('extra_work', deptGroup);
  
  const signatories = chain.map(step => ({
    title: step.role === 'employee' ? 'Signature of Applicant' : `Signature of ${getStageLabelForRole(step.role)}`,
    name: step.role === 'employee' ? (user?.employeeName || user?.displayName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '') : null,
    date: step.role === 'employee' ? new Date() : null,
  }));

  // Loading state
  if (dayCheck.loading) {
    return (
      <div>
        <WRMSFormHeader formTitle="Extra Work Form" formSubtitle="Digi Sahyadri — Request System" />
        <div className="wrms-form-body" style={{ textAlign: 'center', padding: '60px' }}>
          <div className="spinner-border text-primary" role="status" />
          <p style={{ marginTop: '16px', color: '#64748b' }}>Checking work schedule...</p>
        </div>
      </div>
    );
  }



  return (
    <div>
      <WRMSFormHeader formTitle="Extra Work Form" formSubtitle="Digi Sahyadri — Request System" />

      <div className="wrms-form-body">
        {/* Day type indicator */}
        <div className="wrms-balance-info" style={{ marginBottom: '24px', background: '#ecfdf5', borderColor: '#a7f3d0' }}>
          <FaInfoCircle style={{ color: '#059669', flexShrink: 0 }} />
          <div style={{ fontSize: '0.8rem', color: '#065f46' }}>
            <strong>{dayCheck.description}</strong> — Extra Work form is available for this date.
            Approved extra work may generate Comp Off credit or Overtime pay (configurable by HR).
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Auto-populated */}
          <div className="wrms-section-label">Employee Details (Auto-populated)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div className="wrms-form-group">
              <label>Name</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.employeeName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || ''} readOnly />
            </div>
            <div className="wrms-form-group">
              <label>Team ID</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.employeeCode || user?.teamId || '—'} readOnly />
            </div>
            <div className="wrms-form-group">
              <label>Designation</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.designation || '—'} readOnly />
            </div>
            <div className="wrms-form-group">
              <label>Department</label>
              <input className="wrms-form-control wrms-field-readonly form-control"
                value={user?.department || '—'} readOnly />
            </div>
          </div>

          {/* Work Details */}
          <div className="wrms-section-label">Extra Work Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div className="wrms-form-group">
              <label>Work Date <span style={{ color: '#dc2626' }}>*</span></label>
              <input type="date" className="wrms-form-control form-control"
                value={form.workDate}
                onChange={e => handleChange('workDate', e.target.value)} required />
            </div>
            <div className="wrms-form-group">
              <label>Project / Client Name</label>
              <input type="text" className="wrms-form-control form-control"
                placeholder="Project or client name"
                value={form.projectClient}
                onChange={e => handleChange('projectClient', e.target.value)} />
            </div>
          </div>

          {/* Work Type */}
          <div className="wrms-form-group" style={{ marginBottom: '20px' }}>
            <label>Work Type <span style={{ color: '#dc2626' }}>*</span></label>
            <div className="wrms-radio-group" style={{ marginTop: '8px' }}>
              {WORK_TYPES.map(wt => (
                <label key={wt.value}
                  className={`wrms-radio-btn ${form.workType === wt.value ? 'selected' : ''}`}>
                  <input type="radio" name="workType" value={wt.value}
                    checked={form.workType === wt.value}
                    onChange={e => handleChange('workType', e.target.value)}
                    style={{ display: 'none' }} />
                  {wt.label}
                </label>
              ))}
            </div>
          </div>

          {/* Time & Hours */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div className="wrms-form-group">
              <label>Start Time <span style={{ color: '#dc2626' }}>*</span></label>
              <input type="time" className="wrms-form-control form-control"
                value={form.startTime}
                onChange={e => handleChange('startTime', e.target.value)} required />
            </div>
            <div className="wrms-form-group">
              <label>End Time <span style={{ color: '#dc2626' }}>*</span></label>
              <input type="time" className="wrms-form-control form-control"
                value={form.endTime}
                onChange={e => handleChange('endTime', e.target.value)} required />
            </div>
            <div className="wrms-form-group">
              <label>Total Hours (Auto-calculated)</label>
              <div className="wrms-days-badge" style={{ marginTop: '6px' }}>
                <FaClock size={12} />
                {form.totalHours} hours
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="wrms-form-group" style={{ marginBottom: '20px' }}>
            <label>Work Description <span style={{ color: '#dc2626' }}>*</span></label>
            <textarea className="wrms-form-control form-control" rows={3}
              placeholder="Describe the work that was done..."
              value={form.description}
              onChange={e => handleChange('description', e.target.value)} required />
          </div>

          {/* Output */}
          <div className="wrms-form-group" style={{ marginBottom: '20px' }}>
            <label>Output Achieved</label>
            <textarea className="wrms-form-control form-control" rows={3}
              placeholder="What was delivered or achieved?"
              value={form.outputAchieved}
              onChange={e => handleChange('outputAchieved', e.target.value)} />
          </div>



          <SignatureFooter signatories={signatories} />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
            <button type="button" className="wrms-btn-outline"
              onClick={() => navigate('/wrms')} style={{ padding: '10px 24px' }}>
              Cancel
            </button>
            <button type="submit" className="wrms-btn-primary" disabled={submitting}>
              <FaPaperPlane size={13} />
              {submitting ? 'Submitting...' : 'Submit Extra Work'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExtraWorkForm;
